import {
  Avatar,
  Box,
  Button,
  CountrySelector,
  Link,
  TextField,
  Typography,
} from '@cambridgeassessment/cambridge-ui';
import { ValidationType } from '@cambridgeassessment/cambridge-ui/build/components/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_PERSONAL_INFO_FORM_SUCCESS_MESSAGE,
  EMAIL_GDPR,
} from 'src/constants';
import useAuth from 'src/hooks/useAuth';
import useNotifications from 'src/hooks/useNotifications';
import {
  getAccountInfo,
  updatePersonalInfo,
} from 'src/services/gigya/gigya-service';
import { validationHandler } from 'src/utils/validator';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import PhotoUploadModal from '../PhotoUploadModal/PhotoUploadModal';
import { Countries } from './Countries';
import { useStyles } from './styles';

type InputField = {
  value: string;
  helperText?: string;
  validationType?: ValidationType;
  isValid: boolean;
  showErr: boolean;
};

const ChangePersonalInfoForm: React.FC = () => {
  const classes = useStyles();
  const { flags } = useFeatureFlags();
  const showProfilePhoto = flags['mycambridge.profile-photo'];
  const { addNotification } = useNotifications();
  const { user, update } = useAuth();
  const [accountInfo, setAccountInfo] = useState<any>({
    profile: {
      photoURL: user?.profilePicture,
    },
  });
  const validator = validationHandler(['required', 'length128']);
  const [firstName, setFirstName] = useState<InputField>({
    value: user.firstName,
    ...validator.validateInput(user.firstName),
    showErr: false,
  });
  const [lastName, setLastName] = useState<InputField>({
    value: user.lastName,
    ...validator.validateInput(user.lastName),
    showErr: false,
  });
  const [activeCountry, setActiveCountry] = useState<string | null>(null);
  const [openPhotoUploadModal, setOpenPhotoUploadModal] =
    useState<boolean>(false);

  useEffect(() => {
    getAccountInfo(
      (response) => {
        setAccountInfo(response);
        setActiveCountry(response.profile.country);
      },
      ['profile']
    );
  }, []);

  const updateFirstOrLastName = useCallback(
    (prev, value) => {
      return { ...prev, value, ...validator.validateInput(value) };
    },
    [validator]
  );

  const handleFirstNameChange = useCallback(
    (e) => {
      const value = e?.target.value;
      setFirstName((prev) => {
        return updateFirstOrLastName(prev, value);
      });
    },
    [updateFirstOrLastName]
  );

  const handleLastNameChange = useCallback(
    (e) => {
      const value = e?.target.value;
      setLastName((prev) => {
        return updateFirstOrLastName(prev, value);
      });
    },
    [updateFirstOrLastName]
  );

  const handleFirstNameErrText = useCallback(() => {
    setFirstName((prev) => ({
      ...prev,
      showErr: true,
    }));
  }, []);

  const handleLastNameErrText = useCallback(() => {
    setLastName((prev) => ({
      ...prev,
      showErr: true,
    }));
  }, []);

  const resetValues = useCallback(() => {
    setFirstName({
      value: user.firstName,
      ...validator.validateInput(user.firstName),
      showErr: false,
    });
    setLastName({
      value: user.lastName,
      ...validator.validateInput(user.lastName),
      showErr: false,
    });
  }, [user.firstName, user.lastName, validator]);

  const handleCountryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setActiveCountry(event.target.value);
    },
    []
  );

  const isNameChanged = useMemo(() => {
    return (
      firstName.value.trim() === user.firstName &&
      lastName.value.trim() === user.lastName
    );
  }, [firstName.value, lastName.value, user.firstName, user.lastName]);

  const isCountryChanged = useMemo(() => {
    return user.country === activeCountry;
  }, [activeCountry, user.country]);

  const isFormValueChanged = useMemo(
    () => isNameChanged && isCountryChanged,
    [isNameChanged, isCountryChanged]
  );

  const isSubmitDisabled = useMemo(
    () =>
      firstName.value.length === 0 ||
      lastName.value.length === 0 ||
      !firstName.isValid ||
      !lastName.isValid ||
      isFormValueChanged,
    [
      firstName.value,
      lastName.value,
      firstName.isValid,
      lastName.isValid,
      isFormValueChanged,
    ]
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      try {
        await updatePersonalInfo(
          firstName.value.trim(),
          lastName.value.trim(),
          activeCountry,
          (callbackResponse) => {
            callbackResponse.status === 'OK'
              ? addNotification({
                  title: DEFAULT_PERSONAL_INFO_FORM_SUCCESS_MESSAGE,
                  variant: 'success',
                })
              : addNotification({
                  title: DEFAULT_ERROR_MESSAGE,
                  variant: 'error',
                });
            update();
          }
        );
      } catch (err) {
        addNotification({
          title: DEFAULT_ERROR_MESSAGE,
          variant: 'error',
        });
        resetValues();
      }
    },
    [
      activeCountry,
      addNotification,
      firstName.value,
      lastName.value,
      resetValues,
      update,
    ]
  );

  const handlePhotoModalChange = (open: boolean) => {
    setOpenPhotoUploadModal(open);
  };

  const handlePhotoUpdate = (accountInfo: any) => {
    setAccountInfo(accountInfo);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="personalInfoForm">
      {showProfilePhoto && (
        <Box mt={4}>
          <Typography variant="h5">Photo</Typography>
          <Box mt={3} display="flex" flexDirection="vertical">
            <Avatar src={accountInfo?.profile?.photoURL} />
            <Box ml={4}>
              <Button
                variant="outlined"
                className={classes.uploadPhoto}
                color="primary"
                data-testid="upload-button"
                size="small"
                onClick={() => handlePhotoModalChange(true)}
              >
                Upload photo
              </Button>
            </Box>
          </Box>
          <PhotoUploadModal
            data-testid="modal-window"
            open={openPhotoUploadModal}
            updatePhoto={handlePhotoUpdate}
            onClose={() => handlePhotoModalChange(false)}
          />
        </Box>
      )}
      <Box mt={4} mb={4}>
        <Typography variant="h5">Personal details</Typography>
        <Typography className={classes.subtitle}>
          Changes to your name will be reflected across all your Cambridge
          products.
        </Typography>
        <Box mt={3}>
          <TextField
            className={classes.inputField}
            id="firstName"
            value={firstName.value}
            onChange={handleFirstNameChange}
            onBlur={handleFirstNameErrText}
            label="First or given name"
            validationType={
              firstName.showErr && !firstName.isValid ? 'error' : null
            }
            helperText={
              firstName.showErr && !firstName.isValid && firstName.helperText
            }
            data-testid="textField-firstName"
          />
          <TextField
            className={classes.inputField}
            id="lastName"
            value={lastName.value}
            onChange={handleLastNameChange}
            onBlur={handleLastNameErrText}
            label="Last name or surname"
            validationType={
              lastName.showErr && !lastName.isValid ? 'error' : null
            }
            helperText={
              lastName.showErr && !lastName.isValid && lastName.helperText
            }
            data-testid="textField-lastName"
          />
          <CountrySelector
            className={classes.countryField}
            value={activeCountry}
            onChange={handleCountryChange}
            label="Location"
            hintText="We need this to comply with privacy laws"
            data-testid="countrySelector"
            countries={Countries}
          />
        </Box>
      </Box>
      <Button
        color="primary"
        disabled={isSubmitDisabled}
        type="submit"
        data-testid="button-save"
      >
        Save changes
      </Button>
      <Typography className={classes.gdpr}>
        If you have a privacy or data protection enquiry, including an enquiry
        related to GDPR (General Data Protection Regulation), or deleting your
        account,{' '}
        <Link href={`mailto:${EMAIL_GDPR}`} className={classes.gdprr}>
          contact our GDPR team
        </Link>
        .
      </Typography>
    </form>
  );
};

export default ChangePersonalInfoForm;
